import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useState, useRef, useEffect, useCallback } from 'preact/hooks';
import { Phone as PhoneIcon, Loader2, Info } from 'lucide-preact';
import { leadsApi } from '@/api/leads';
import { audienceApi } from '@/api/audience';
import { useToast } from '@/hooks/useToast';
/**
 * Reusable component for displaying phone with enrichment and update capabilities.
 * Shows phone number, enrich button (if available), and update indicator (if new phone available).
 */
export const PhoneEnrichmentCell = ({ phone, globalContactPhone, phoneEnrichmentAvailableDate, id, clientCompanyId, workspaceId, campaignId, onPhoneUpdated, isLastRow = false, context = 'lead', canEdit = true }) => {
    const [isEnriching, setIsEnriching] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState(isLastRow ? 'top' : 'bottom');
    const enrichButtonRef = useRef(null);
    const abortControllerRef = useRef(null);
    const { showSuccess, showError } = useToast();
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);
    // Update tooltip position based on isLastRow prop
    useEffect(() => {
        setTooltipPosition(isLastRow ? 'top' : 'bottom');
    }, [isLastRow]);
    // Detect if tooltip would overflow viewport and adjust position
    const checkTooltipPosition = useCallback(() => {
        if (!enrichButtonRef.current)
            return;
        // If it's the last row, always show above
        if (isLastRow) {
            setTooltipPosition('top');
            return;
        }
        const rect = enrichButtonRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const tooltipHeight = 120; // Approximate height of tooltip with padding
        const spaceBelow = viewportHeight - rect.bottom;
        // If not enough space below, show tooltip above
        setTooltipPosition(spaceBelow < tooltipHeight ? 'top' : 'bottom');
    }, [isLastRow]);
    // Check position on hover
    useEffect(() => {
        const button = enrichButtonRef.current;
        if (!button)
            return;
        const handleMouseEnter = () => checkTooltipPosition();
        button.addEventListener('mouseenter', handleMouseEnter);
        return () => {
            button.removeEventListener('mouseenter', handleMouseEnter);
        };
    }, [checkTooltipPosition]);
    const canEnrich = !phoneEnrichmentAvailableDate || new Date(phoneEnrichmentAvailableDate) <= new Date();
    const hasUpdatedPhone = globalContactPhone && globalContactPhone !== phone;
    const handleEnrichPhone = async (e) => {
        e.stopPropagation();
        if (!canEnrich || isEnriching)
            return;
        abortControllerRef.current?.abort();
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        setIsEnriching(true);
        try {
            const resp = context === 'fileImportRow'
                ? await audienceApi.enrichPhone(clientCompanyId, workspaceId, campaignId, id, abortController.signal)
                : await leadsApi.enrichPhone(workspaceId, campaignId, id, abortController.signal);
            const updated = resp?.data;
            if (updated) {
                const updatePayload = {
                    Phone: updated.Phone,
                    GlobalContactPhone: updated.GlobalContactPhone,
                    PhoneEnrichmentAvailableDate: updated.PhoneEnrichmentAvailableDate
                };
                onPhoneUpdated?.(updatePayload);
                showSuccess('Phone enrichment successful', '');
            }
        }
        catch (err) {
            if (err instanceof Error && err.name === 'AbortError')
                return;
            showError('Failed to enrich phone', '');
        }
        finally {
            if (!abortController.signal.aborted) {
                setIsEnriching(false);
            }
        }
    };
    const handleUpdatePhone = async (e) => {
        e.stopPropagation();
        if (!hasUpdatedPhone || isUpdating)
            return;
        abortControllerRef.current?.abort();
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        setIsUpdating(true);
        try {
            const resp = context === 'fileImportRow'
                ? await audienceApi.syncPhone(clientCompanyId, workspaceId, campaignId, id, abortController.signal)
                : await leadsApi.syncPhone(workspaceId, campaignId, id, abortController.signal);
            const updated = resp?.data;
            if (updated) {
                const updatePayload = {
                    Phone: updated.Phone,
                    GlobalContactPhone: updated.GlobalContactPhone,
                    PhoneEnrichmentAvailableDate: updated.PhoneEnrichmentAvailableDate
                };
                onPhoneUpdated?.(updatePayload);
                showSuccess('Phone updated', '');
            }
        }
        catch (err) {
            if (err instanceof Error && err.name === 'AbortError')
                return;
            showError('Failed to update phone', '');
        }
        finally {
            if (!abortController.signal.aborted) {
                setIsUpdating(false);
            }
        }
    };
    return (_jsxs("div", { class: "flex items-center gap-2", children: [_jsx("span", { class: "text-gray-700 dark:text-gray-300", children: phone || '—' }), canEdit && _jsxs("div", { class: "relative group/tooltip", children: [_jsx("button", { ref: enrichButtonRef, type: "button", disabled: !canEnrich || isEnriching, onClick: handleEnrichPhone, class: "inline-flex items-center justify-center w-6 h-6 rounded hover:bg-primary-50 dark:hover:bg-primary-900/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors", children: isEnriching ? (_jsx(Loader2, { class: "w-3.5 h-3.5 text-primary-600 dark:text-primary-400 animate-spin" })) : (_jsx(PhoneIcon, { class: "w-3.5 h-3.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400" })) }), !canEnrich && phoneEnrichmentAvailableDate && (_jsx("div", { class: `absolute right-0 hidden group-hover/tooltip:block min-w-[280px] px-3 py-2 text-xs rounded-md shadow-lg z-50 pointer-events-none bg-gray-800 dark:bg-gray-700 text-gray-200 border border-gray-700 dark:border-gray-600 ${tooltipPosition === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2'}`, children: _jsxs("div", { class: "flex items-start gap-2", children: [_jsx(Info, { class: "w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-primary-400" }), _jsxs("div", { class: "flex-1 leading-relaxed break-words whitespace-normal", children: ["Phone enrichment available again on ", new Date(phoneEnrichmentAvailableDate).toLocaleDateString()] })] }) }))] }), canEdit && hasUpdatedPhone && (_jsxs("div", { class: "relative group/update flex items-center", children: [_jsx("div", { class: "w-2 h-2 rounded-full bg-primary-500 cursor-pointer" }), _jsx("div", { class: "absolute top-0 left-1/2 -translate-x-1/2 w-16 h-20 hidden group-hover/update:block" }), _jsx("div", { class: "absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover/update:block z-50", children: _jsx("div", { class: "w-56 px-3 py-2 text-xs rounded-md shadow-lg bg-gray-800 dark:bg-gray-700 text-gray-200 border border-gray-700 dark:border-gray-600", children: _jsxs("div", { class: "flex flex-col gap-2", children: [_jsxs("div", { class: "flex items-start gap-2", children: [_jsx(Info, { class: "w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-primary-400" }), _jsxs("div", { children: [_jsx("div", { class: "font-medium", children: "Updated phone available" }), _jsx("div", { class: "text-gray-400 mt-1", children: globalContactPhone })] })] }), _jsx("button", { type: "button", disabled: isUpdating, onClick: handleUpdatePhone, class: "w-full px-2 py-1.5 text-xs font-medium rounded bg-primary-600 hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors", children: isUpdating ? 'Updating...' : 'Update Phone' })] }) }) })] }))] }));
};
